import React, { useState, useRef } from 'react';
import { useAppContext, JOB_TYPES, JOB_CATEGORIES } from '../../context/AppContext';
import supabase from '../../supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import RichTextEditor from '../../components/common/RichTextEditor';

const {
  FiUser, FiMail, FiBriefcase, FiMapPin, FiCalendar,
  FiImage, FiCheckCircle, FiAlertCircle, FiLoader, FiArrowRight,
  FiZap, FiX, FiLink, FiPlus
} = FiIcons;

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full bg-stone-50 border border-stone-200 rounded-2xl px-5 py-4 text-sm font-bold text-stone-900 focus:ring-2 focus:ring-violet-500 outline-none transition-all";
const selectCls = inputCls;

const MigrationTool = () => {
  const { uploadLogo } = useAppContext();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    email: '', org_name: '', job_title: '', description: '',
    category: 'Seasonal', job_type: 'Bicycle',
    listing_type: 'free', expiration_date: '',
    application_method: 'link', application_link: '', application_instructions: ''
  });
  
  const [locations, setLocations] = useState([{ name: '' }]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleLocationChange = (index, value) => {
    const newLocs = [...locations];
    if (newLocs[index]) {
      newLocs[index].name = value;
      setLocations(newLocs);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      let logoUrl = null;
      if (logoFile) {
        const uploadResult = await uploadLogo(logoFile);
        if (uploadResult?.success) {
          logoUrl = uploadResult.url;
        }
      }

      const validLocs = locations.filter(l => l.name && l.name.trim());

      // Call the Secure Edge Function for Migration
      const { data, error: funcError } = await supabase.functions.invoke('admin_migrate_employer', {
        body: {
          ...form,
          locations: validLocs,
          logo_url: logoUrl
        }
      });

      if (funcError) throw funcError;
      if (data?.error) throw new Error(data.error);

      setResult({
        employerEmail: form.email.toLowerCase().trim(),
        orgName: form.org_name,
        isNewAccount: data.isNewAccount,
        jobTitle: data.jobTitle,
        expiresAt: data.expiresAt,
        welcomeEmailSent: data.welcomeEmailSent
      });

      // Clear form
      setForm({
        email: '', org_name: '', job_title: '', description: '',
        category: 'Seasonal', job_type: 'Bicycle',
        listing_type: 'free', expiration_date: '',
        application_method: 'link', application_link: '', application_instructions: ''
      });
      setLocations([{ name: '' }]);
      setLogoFile(null);
      setLogoPreview(null);

    } catch (err) {
      console.error('Migration Error:', err);
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-10">
      <div className="bg-white rounded-[40px] border border-stone-200 p-8 md:p-12 shadow-sm">
        <div className="flex items-center gap-4 mb-2">
          <div className="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center text-violet-600 shrink-0">
            <SafeIcon icon={FiArrowRight} className="text-xl" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-stone-900 uppercase tracking-tight">Migration Tool</h2>
            <p className="text-stone-500 font-bold uppercase tracking-widest text-[10px]">Securely import employer accounts and job listings</p>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-emerald-50 border border-emerald-200 rounded-[32px] p-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 bg-emerald-500 rounded-2xl flex items-center justify-center text-white shrink-0">
                <SafeIcon icon={FiCheckCircle} className="text-lg" />
              </div>
              <div>
                <h3 className="font-black text-emerald-900 uppercase tracking-tight text-lg">Migration Successful</h3>
                <p className="text-emerald-700 font-bold text-[10px] uppercase tracking-widest">Job is live immediately</p>
              </div>
              <button onClick={() => setResult(null)} className="ml-auto p-2 hover:bg-emerald-100 rounded-xl text-emerald-700">
                <SafeIcon icon={FiX} />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { label: 'Employer', value: result.employerEmail },
                { label: 'Account', value: result.isNewAccount ? 'New Account Created' : 'Existing Account' },
                { label: 'Job Title', value: result.jobTitle },
                { label: 'Expires At', value: new Date(result.expiresAt).toLocaleDateString() },
                { label: 'Welcome Email', value: result.isNewAccount ? (result.welcomeEmailSent ? 'Sent ✓' : 'Failed') : 'Skipped' },
              ].map(({ label, value }) => (
                <div key={label} className="bg-white rounded-2xl p-5 border border-emerald-100">
                  <p className="text-[9px] font-black text-stone-400 uppercase tracking-widest mb-1">{label}</p>
                  <p className="font-black text-sm text-stone-900">{value}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit} className="bg-white rounded-[40px] border border-stone-200 p-8 md:p-12 shadow-sm space-y-10">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-3">
            <SafeIcon icon={FiAlertCircle} className="text-red-500 shrink-0" />
            <p className="text-red-700 font-bold text-sm">{error}</p>
          </div>
        )}

        <div>
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone-100">
            <SafeIcon icon={FiUser} className="text-sm text-violet-600" />
            <h3 className="font-black text-stone-900 uppercase tracking-widest text-xs">Employer Details</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Employer Email">
              <input required type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} />
            </Field>
            <Field label="Company Name">
              <input required type="text" className={inputCls} value={form.org_name} onChange={e => set('org_name', e.target.value)} />
            </Field>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone-100">
            <SafeIcon icon={FiImage} className="text-sm text-violet-600" />
            <h3 className="font-black text-stone-900 uppercase tracking-widest text-xs">Company Logo</h3>
          </div>
          <div onClick={() => fileInputRef.current?.click()} className="h-32 bg-stone-50 border-2 border-dashed border-stone-200 rounded-2xl flex items-center justify-center cursor-pointer overflow-hidden">
            {logoPreview ? <img src={logoPreview} className="max-h-full max-w-full object-contain p-4" /> : <p className="text-[10px] font-black uppercase text-stone-400">Click to upload</p>}
          </div>
          <input type="file" ref={fileInputRef} onChange={handleLogoSelect} className="hidden" accept="image/*" />
        </div>

        <div>
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone-100">
            <SafeIcon icon={FiBriefcase} className="text-sm text-violet-600" />
            <h3 className="font-black text-stone-900 uppercase tracking-widest text-xs">Job Details</h3>
          </div>
          <div className="space-y-6">
            <Field label="Job Title">
              <input required type="text" className={inputCls} value={form.job_title} onChange={e => set('job_title', e.target.value)} />
            </Field>
            <Field label="Description">
              <RichTextEditor 
                value={form.description} 
                onChange={val => set('description', val)} 
                className="focus-within:ring-violet-500"
              />
            </Field>
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Category">
                <select className={selectCls} value={form.category} onChange={e => set('category', e.target.value)}>
                  {JOB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Type">
                <select className={selectCls} value={form.job_type} onChange={e => set('job_type', e.target.value)}>
                  {JOB_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest">Locations</label>
                <button type="button" onClick={() => setLocations([...locations, { name: '' }])} className="text-violet-600 text-[10px] font-black uppercase tracking-widest flex items-center gap-1 hover:text-violet-700 transition-colors">
                  <SafeIcon icon={FiPlus} /> Add Another
                </button>
              </div>
              <div className="space-y-3">
                {locations.map((loc, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="relative flex-1">
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-400">
                        <SafeIcon icon={FiMapPin} />
                      </div>
                      <input 
                        className={inputCls + " pl-12"} 
                        value={loc.name} 
                        onChange={e => handleLocationChange(i, e.target.value)} 
                        placeholder="City, State / Remote" 
                      />
                    </div>
                    {locations.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => setLocations(locations.filter((_, idx) => idx !== i))} 
                        className="w-14 h-14 bg-red-50 text-red-500 hover:bg-red-100 rounded-2xl flex items-center justify-center transition-colors shrink-0"
                      >
                        <SafeIcon icon={FiX} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* REPLICATED APPLICATION DETAILS FROM POSTJOB.JSX */}
        <div>
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone-100">
            <SafeIcon icon={FiMail} className="text-sm text-violet-600" />
            <h3 className="font-black text-stone-900 uppercase tracking-widest text-xs">Application Details</h3>
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Method">
                <select 
                  className={selectCls} 
                  value={form.application_method} 
                  onChange={e => set('application_method', e.target.value)}
                >
                  <option value="link">External URL</option>
                  <option value="email">Email Address</option>
                  <option value="text">Custom Instructions</option>
                </select>
              </Field>
              <Field label={form.application_method === 'link' ? 'Application URL' : form.application_method === 'email' ? 'Contact Email' : 'Short Note'}>
                <input 
                  required 
                  type={form.application_method === 'email' ? 'email' : 'text'} 
                  className={inputCls} 
                  value={form.application_link} 
                  onChange={e => set('application_link', e.target.value)} 
                  placeholder={form.application_method === 'link' ? 'https://...' : form.application_method === 'email' ? 'jobs@company.com' : 'Apply via our portal'} 
                />
              </Field>
            </div>
            {form.application_method === 'text' && (
              <Field label="Detailed Instructions">
                <textarea 
                  rows={3} 
                  className={inputCls + ' resize-none'} 
                  value={form.application_instructions} 
                  onChange={e => set('application_instructions', e.target.value)} 
                  placeholder="List any specific steps or documents required..." 
                />
              </Field>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-stone-100">
            <SafeIcon icon={FiZap} className="text-sm text-violet-600" />
            <h3 className="font-black text-stone-900 uppercase tracking-widest text-xs">Listing Options</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Type">
              <select className={selectCls} value={form.listing_type} onChange={e => set('listing_type', e.target.value)}>
                <option value="free">Standard / Free</option>
                <option value="featured">Featured / Paid</option>
              </select>
            </Field>
            <Field label="Old Expiration Date">
              <input required type="date" className={inputCls} value={form.expiration_date} onChange={e => set('expiration_date', e.target.value)} />
            </Field>
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-5 bg-violet-600 text-white rounded-[24px] font-black uppercase tracking-widest text-xs hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-3"
        >
          {submitting ? <><SafeIcon icon={FiLoader} className="animate-spin" /> Processing Migration...</> : 'Migrate Employer & Job'}
        </button>
      </form>
    </div>
  );
};

export default MigrationTool;